import { v4 as uuidv4 } from 'uuid';

import HaspBar from './HaspBar';
import CustomRect from '../draw/CustomRect';
import { executeActions } from '../actions/previewActionDispatcher.js';
import { lvglBarIndicatorBounds } from './barGeometry.js';
import { OPENHASP_CONTROL_TYPE } from './controlTypes.js';
import {
    knobPositionFromBounds,
    resolveSliderKnobPadding,
    sliderKnobBounds,
    trackPositionToValue,
    valueToTrackPosition,
} from './sliderGeometry.js';
import { attachImageSourcePreview } from './imageSource.js';
import { resolvePadding } from './properties/v2/padding.js';

export default class HaspSlider extends HaspBar {
    // hassTemplate = '/media/templates/slider.ftl';

    constructor(config) {
        super(config);

        // Knob radius has no schema/theme default — it defaults to a perfect
        // circle sized to the knob's own dimensions. Compute and persist that
        // once (silently — this is an internal default being filled in, not
        // a user edit) if nothing already set it.
        const knob = this.style('knob');
        const knobPadding = resolveSliderKnobPadding(knob);
        const baseKnobSize = Math.min(config.width, config.height);
        const defaultKnobRadius = (baseKnobSize + knobPadding.left + knobPadding.right) / 2;
        if (knob.radius === undefined || knob.radius === null) {
            this.pokeStyle('knob', 'radius', defaultKnobRadius);
        }

        const initialBounds = this.knobBoundsAt(0);

        this.knob = new CustomRect({
            id: uuidv4(),
            x: initialBounds.x,
            y: initialBounds.y,
            width: initialBounds.width,
            height: initialBounds.height,
            obj: "knob",
            draggable: true,
            listening: true,
            borderWidth: Number(knob.border_width ?? 0),
            cornerRadius: Number(knob.radius ?? 0),
        });

        // LVGL RANGE has two knobs. Keep the second knob in the scene graph so
        // switching type at runtime does not recreate the widget; it is only
        // made visible for the public openHASP RANGE mode.
        this.leftKnob = new CustomRect({
            id: uuidv4(),
            x: initialBounds.x,
            y: initialBounds.y,
            width: initialBounds.width,
            height: initialBounds.height,
            obj: "knob",
            draggable: true,
            listening: true,
            borderWidth: Number(knob.border_width ?? 0),
            cornerRadius: Number(knob.radius ?? 0),
        });
        this.leftKnob.visible(false);

        // Construction, import, duplication and later style/state refreshes all
        // consume the exact same resolved knob style. Do not seed Konva with
        // independent defaults here: that was why border_color started as black
        // until the property was edited once.
        this.applyKnobStyle();

        //enables the button to change thye value
        this.knob.on('dragmove', (e) => {
            this.updateKnob();
            // Keep the pointer-driven knob position intact. Re-applying the rounded
            // value here used to move a vertical knob by half its height on every
            // drag event, fighting Konva's drag position and producing jitter/trails.
            const notify = () => this.adjust(undefined, undefined, false);
            if (this.withPreviewMutation) this.withPreviewMutation(notify);
            else notify();
            // Dragging the knob is the slider's "changed" event, so preview-capable
            // actions bound to it run live with the value the drag just produced.
            executeActions(this, 'changed', this.previewValueContext());
        });
        this.leftKnob.on('dragmove', () => {
            this.updateStartKnob();
            const notify = () => this.adjust(undefined, undefined, false);
            if (this.withPreviewMutation) this.withPreviewMutation(notify);
            else notify();
            executeActions(this, 'changed', this.previewValueContext());
        });
        // Resizing must not read the value back out of the old knob position — that let
        // a resize silently change val. adjust() re-derives the geometry from val instead.
        this.on('transform', function () {
            this.updateSize();
            this.adjust();
        });
        this.on('transformend', (e) => {
            this.updateSize();
            this.adjust();
        });
        for (const prop of ['bg_color', 'bg_grad_color', 'bg_grad_dir', 'bg_main_stop', 'bg_grad_stop', 'border_color', 'border_width', 'radius']) {
            this.onStyle('knob', prop, () => this.applyKnobStyle());
        }
        // Padding changes both the rendered size and the position of the
        // padded bounds, so refresh the complete geometry from the value.
        for (const prop of ['padding', 'pad_left', 'pad_right', 'pad_top', 'pad_bottom']) {
            this.onStyle('knob', prop, () => {
                this.updateSize();
                this.syncKnobFromValue();
            });
        }

        // The bar already re-derives the indicator from val/min/max; the knob has to
        // follow the same way round, otherwise a loaded value never reaches the geometry.
        this.on('data_val', () => this.syncKnobFromValue());
        this.on('data_min', () => this.syncKnobFromValue());
        this.on('data_max', () => this.syncKnobFromValue());
        this.on('data_step', () => this.syncKnobFromValue());
        this.on('data_start_value', () => this.syncKnobFromValue());
        this.on('type', () => this.syncKnobFromValue());

        this.updateSize();
        this.add(this.knob);
        this.add(this.leftKnob);
        this.syncKnobFromValue();

        this.knobImagePreview = attachImageSourcePreview(this, {
            part: 'knob',
            getBounds: () => ({
                x: this.knob.x(),
                y: this.knob.y(),
                width: this.knob.width(),
                height: this.knob.height(),
                // LVGL clips the knob pattern to the widget draw area. Keep
                // the native image placement in the padded knob bounds, but
                // do not let it paint beyond the slider object in preview.
                clipBounds: this.clipToTrack({
                    x: this.knob.x(),
                    y: this.knob.y(),
                    width: this.knob.width(),
                    height: this.knob.height(),
                }),
                clipRadius: this.styleProp('knob', 'radius'),
                visible: this.knob.visible(),
            }),
        });
        this.add(this.knobImagePreview.imageNode);

        this.leftKnobImagePreview = attachImageSourcePreview(this, {
            part: 'knob',
            getBounds: () => ({
                x: this.leftKnob.x(),
                y: this.leftKnob.y(),
                width: this.leftKnob.width(),
                height: this.leftKnob.height(),
                clipBounds: this.clipToTrack({
                    x: this.leftKnob.x(),
                    y: this.leftKnob.y(),
                    width: this.leftKnob.width(),
                    height: this.leftKnob.height(),
                }),
                clipRadius: this.styleProp('knob', 'radius'),
                visible: this.leftKnob.visible(),
            }),
        });
        this.add(this.leftKnobImagePreview.imageNode);

    }

    setNewSize(size) {
        super.setNewSize(size);
        this.updateSize();
        this.syncKnobFromValue();
    }

    updateSize() {
        super.updateSize();
        if (this.knob !== undefined) {
            this.applyKnobBounds(this.knobBoundsAt(0));
            if (this.leftKnob) this.applyKnobBounds(this.knobBoundsAt(0), this.leftKnob);
            this.applyKnobStyle();
        }
    }

    knobPads() {
        return resolveSliderKnobPadding(this.style('knob'));
    }

    knobBoundsAt(position) {
        return sliderKnobBounds({
            position,
            width: this.width(),
            height: this.height(),
            horizontal: this.isHorizontal,
            ...this.knobPads(),
        });
    }

    applyKnobBounds(bounds, knob = this.knob) {
        if (!knob) return;
        knob.x(bounds.x);
        knob.y(bounds.y);
        knob.width(bounds.width);
        knob.height(bounds.height);
        if (knob === this.leftKnob) this.leftKnobImagePreview?.updateBounds();
        else this.knobImagePreview?.updateBounds();
    }

    /** Apply the currently resolved default/theme/local/state knob style to Konva. */
    applyKnobStyle() {
        if (!this.knob) return;
        const knob = this.style('knob');
        if (this.applyBackgroundStyle) this.applyBackgroundStyle(this.knob, 'knob');
        else this.knob.fill(knob.bg_color ?? null);
        this.knob.stroke(knob.border_color ?? null);
        if (this.knob.setStrokeWidth) this.knob.setStrokeWidth(Number(knob.border_width ?? 0));
        else this.knob.strokeWidth(Number(knob.border_width ?? 0));
        this.knob.cornerRadius(Number(knob.radius ?? 0));
        if (this.leftKnob) {
            if (this.applyBackgroundStyle) this.applyBackgroundStyle(this.leftKnob, 'knob');
            else this.leftKnob.fill(knob.bg_color ?? null);
            this.leftKnob.stroke(knob.border_color ?? null);
            if (this.leftKnob.setStrokeWidth) this.leftKnob.setStrokeWidth(Number(knob.border_width ?? 0));
            else this.leftKnob.strokeWidth(Number(knob.border_width ?? 0));
            this.leftKnob.cornerRadius(Number(knob.radius ?? 0));
        }
    }

    publicType() {
        return Number(this.type ?? OPENHASP_CONTROL_TYPE.NORMAL);
    }

    previewValueContext() {
        const context = { val: this.val };
        if (this.publicType() === OPENHASP_CONTROL_TYPE.RANGE) context.start = this.start_value;
        return context;
    }

    updateKnob() {
        if (this.knob !== undefined) {
            const pads = this.knobPads();
            const baseSize = this.isHorizontal ? this.height() : this.width();
            if (this.isHorizontal) {
                const center = knobPositionFromBounds({
                    x: this.knob.x(),
                    y: this.knob.y(),
                    width: this.knob.width(),
                    height: baseSize,
                    horizontal: true,
                    ...pads,
                });
                const rawValue = trackPositionToValue(center, this.min, this.max, this.width(), false, this.publicType(), resolvePadding(this.style('main')));
                const value = this.quantizeValue ? this.quantizeValue(rawValue) : Math.round(rawValue);
                if (this.setPreviewProperty) this.setPreviewProperty('val', value);
                else this.val = value;
                this.normalizeDataValues?.();
                this.applyKnobBounds(this.knobBoundsAt(this.valueToTrackPosition(this.val)));
            } else {
                const center = knobPositionFromBounds({
                    x: this.knob.x(),
                    y: this.knob.y(),
                    width: baseSize,
                    height: this.knob.height(),
                    horizontal: false,
                    ...pads,
                });
                const rawValue = trackPositionToValue(center, this.min, this.max, this.height(), true, this.publicType(), resolvePadding(this.style('main')));
                const value = this.quantizeValue ? this.quantizeValue(rawValue) : Math.round(rawValue);
                if (this.setPreviewProperty) this.setPreviewProperty('val', value);
                else this.val = value;
                this.normalizeDataValues?.();
                this.applyKnobBounds(this.knobBoundsAt(this.valueToTrackPosition(this.val)));
            }
        }
        this.idTextObj.moveToTop();
    }

    updateStartKnob() {
        if (!this.leftKnob || this.publicType() !== OPENHASP_CONTROL_TYPE.RANGE) return;
        const pads = this.knobPads();
        const baseSize = this.isHorizontal ? this.height() : this.width();
        const center = knobPositionFromBounds({
            x: this.leftKnob.x(),
            y: this.leftKnob.y(),
            width: this.isHorizontal ? this.leftKnob.width() : baseSize,
            height: this.isHorizontal ? baseSize : this.leftKnob.height(),
            horizontal: this.isHorizontal,
            ...pads,
        });
        const rawValue = trackPositionToValue(
            center,
            this.min,
            this.max,
            this.isHorizontal ? this.width() : this.height(),
            !this.isHorizontal,
            OPENHASP_CONTROL_TYPE.NORMAL,
            resolvePadding(this.style('main')),
        );
        const value = this.quantizeValue ? this.quantizeValue(rawValue) : Math.round(rawValue);
        const startValue = Math.min(value, this.val);
        if (this.setPreviewProperty) this.setPreviewProperty('start_value', startValue);
        else this.start_value = startValue;
        this.normalizeDataValues?.();
        this.applyKnobBounds(this.knobBoundsAt(this.valueToTrackPosition(this.start_value)), this.leftKnob);
    }

    valueToTrackPosition(value) {
        return valueToTrackPosition(
            value,
            this.min,
            this.max,
            this.isHorizontal ? this.width() : this.height(),
            !this.isHorizontal,
            this.publicType() === OPENHASP_CONTROL_TYPE.RANGE
                ? OPENHASP_CONTROL_TYPE.NORMAL
                : this.publicType(),
            resolvePadding(this.style('main')),
        );
    }

    /** Native value-to-pixel mapping for both orientations, without transforms. */
    updateValue() {
        if (!this._deferValueNormalization) this.normalizeDataValues?.();
        const geometry = lvglBarIndicatorBounds({
            type: this.publicType(),
            min: this.min,
            max: this.max,
            value: this.val,
            startValue: this.start_value,
            width: this.width(),
            height: this.height(),
            horizontal: this.isHorizontal,
            padding: resolvePadding(this.style('main')),
        });
        this.setBarIndicatorBounds(geometry);
        const pos = this.valueToTrackPosition(this.val);
        return pos;
    }

    /**
     * Slider geometry has one direction of truth: val/min/max -> indicator + knob.
     * `updateKnob()` is the inverse (knob -> val) and belongs to dragging only, so
     * everything else — construction, load, property edits, resize — comes through here.
     */
    syncKnobFromValue() {
        const pos = this.updateValue();
        if (this.knob) {
            this.applyKnobBounds(this.knobBoundsAt(pos));
        }
        if (this.leftKnob) {
            const range = this.publicType() === OPENHASP_CONTROL_TYPE.RANGE;
            this.leftKnob.visible(range);
            this.leftKnobImagePreview?.updateBounds();
            if (range) {
                const start = Math.min(Number(this.start_value ?? this.min), Number(this.val));
                this.applyKnobBounds(this.knobBoundsAt(this.valueToTrackPosition(start)), this.leftKnob);
            }
        }
        return pos;
    }

    /**
     * the super.adjust() is needed to dispatch the events
     * here we do the knob
     */
    adjust(type, value, syncKnob = true) {
        const liveValueInput = ['data_val', 'data_start_value', 'data_step'].includes(type);
        const previousDefer = this._deferValueNormalization;
        if (liveValueInput) this._deferValueNormalization = true;
        try {
            if (syncKnob) this.syncKnobFromValue();
            else this.updateValue();
            super.adjust(type, value);
        } finally {
            this._deferValueNormalization = previousDefer;
        }
    }

    objectExport(page, objectData, hassConfig) {
        super.objectExport(page, objectData, hassConfig);
    }
}
